import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SelectChangeEvent,
  OutlinedInput,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import {
  StudentSearchFilters,
  CHAT_FREQUENCY_OPTIONS,
  LEARNING_LEVEL_OPTIONS,
  SORT_OPTIONS,
  PREFECTURES,
  GRADE_OPTIONS,
} from '../../types/student-search';

interface StudentSearchFormProps {
  filters: StudentSearchFilters;
  onFiltersChange: (newFilters: Partial<StudentSearchFilters>) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
}

export const StudentSearchForm: React.FC<StudentSearchFormProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onReset,
  loading = false,
}) => {
  const handleChange =
    (field: keyof StudentSearchFilters) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<any>
    ) => {
      const value = event.target.value;
      onFiltersChange({ [field]: value === '' ? undefined : value });
    };

  const handleMultiSelectChange =
    (field: keyof StudentSearchFilters) =>
    (event: SelectChangeEvent<string[] | number[]>) => {
      const value = event.target.value;
      onFiltersChange({ [field]: value.length > 0 ? value : undefined });
    };

  const handleGradeChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    onFiltersChange({ grades: value.length > 0 ? value : undefined });
  };

  const handleNumberChange =
    (field: keyof StudentSearchFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onFiltersChange({
        [field]: value === '' ? undefined : parseInt(value, 10),
      });
    };

  return (
    <Box>
      {/* メイン検索 */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="フリーワード検索"
              placeholder="自己PR、志望動機で検索"
              value={filters.keyword || ''}
              onChange={handleChange('keyword')}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>ソート順</InputLabel>
              <Select
                value={filters.sort || ''}
                onChange={handleChange('sort')}
                label="ソート順"
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={onSearch}
              disabled={loading}
              sx={{ py: 1.5 }}
              startIcon={<SearchIcon />}
            >
              {loading ? '検索中...' : '検索'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* 詳細フィルター */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            詳細検索条件
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* 基本条件 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                基本条件
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {/* 年齢範囲 */}
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="年齢（最小）"
                value={filters.age_min || ''}
                onChange={handleNumberChange('age_min')}
                inputProps={{ min: 18, max: 65 }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="年齢（最大）"
                value={filters.age_max || ''}
                onChange={handleNumberChange('age_max')}
                inputProps={{ min: 18, max: 65 }}
              />
            </Grid>

            {/* 大学名フリーワード検索 */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="大学名"
                placeholder="大学名で検索（例：東京大学、早稲田）"
                value={filters.university_search || ''}
                onChange={handleChange('university_search')}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />
            </Grid>

            {/* 学部名フリーワード検索 */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="学部名"
                placeholder="学部名で検索（例：経済学部、工学部）"
                value={filters.faculty_search || ''}
                onChange={handleChange('faculty_search')}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />
            </Grid>

            {/* 学年 */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>学年</InputLabel>
                <Select
                  multiple
                  value={filters.grades || []}
                  onChange={handleGradeChange}
                  input={<OutlinedInput label="学年" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((value) => (
                        <Chip
                          key={value}
                          label={
                            GRADE_OPTIONS.find((g) => g.value === value)
                              ?.label || value
                          }
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {GRADE_OPTIONS.map((grade) => (
                    <MenuItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 居住地域 */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>居住地域</InputLabel>
                <Select
                  multiple
                  value={filters.current_prefectures || []}
                  onChange={handleMultiSelectChange('current_prefectures')}
                  input={<OutlinedInput label="居住地域" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {PREFECTURES.map((prefecture) => (
                    <MenuItem key={prefecture} value={prefecture}>
                      {prefecture}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 活動条件 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                活動条件
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {/* 学習スコアレベル（3段階チップ） */}
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                学習スコアレベル
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {LEARNING_LEVEL_OPTIONS.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    variant={
                      filters.learning_level === option.value
                        ? 'filled'
                        : 'outlined'
                    }
                    color={
                      filters.learning_level === option.value
                        ? option.value === 'High'
                          ? 'success'
                          : option.value === 'Medium'
                            ? 'warning'
                            : 'error'
                        : 'default'
                    }
                    onClick={() => {
                      onFiltersChange({
                        learning_level:
                          filters.learning_level === option.value
                            ? undefined
                            : option.value,
                      });
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>

            {/* 直近活動トグル */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.recent_activity_within_days === 30}
                    onChange={(event) => {
                      onFiltersChange({
                        recent_activity_within_days: event.target.checked
                          ? 30
                          : undefined,
                      });
                    }}
                  />
                }
                label="30日以内に活動した学生のみ表示"
              />
            </Grid>

            {/* スカウト受信設定 */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.accepts_scout_emails === true}
                    onChange={(event) =>
                      onFiltersChange({
                        accepts_scout_emails: event.target.checked
                          ? true
                          : undefined,
                      })
                    }
                    color="primary"
                  />
                }
                label="スカウト受信許可のみ表示"
              />
            </Grid>

            {/* アクションボタン */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={onSearch}
                  disabled={loading}
                  startIcon={<SearchIcon />}
                >
                  {loading ? '検索中...' : '検索実行'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={onReset}
                  startIcon={<RefreshIcon />}
                >
                  条件リセット
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
